import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query'

import { successToast } from 'common/components/toastNotification'

import { pinContent } from '../api'
import { ContentLibraryEntry } from '../queries/useGetContentLibraryQuery'
import { contentKeys } from '../queryKeys'

type ContentLibraryQueryData = {
  data: ContentLibraryEntry[]
  total: number
  pinnedContents: ContentLibraryEntry[]
}

type PinMutationContext = {
  previousQueries: [QueryKey, ContentLibraryQueryData | undefined][]
}

const usePinContentMutation = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ accountId, contentId }: { accountId: string; contentId: string }) =>
      pinContent({ accountId, contentId }),
    {
      onMutate: async ({ contentId }): Promise<PinMutationContext> => {
        await queryClient.cancelQueries(contentKeys.contentLibraryAll())
        const previousQueries =
          queryClient.getQueriesData<ContentLibraryQueryData>(
            contentKeys.contentLibraryAll()
          )

        previousQueries.forEach(([queryKey]) => {
          queryClient.setQueryData<ContentLibraryQueryData>(queryKey, (old) => {
            if (!old) {
              return old
            }

            const data = old.data.map((entry) =>
              entry.id === contentId ? { ...entry, isPinned: true } : entry
            )
            const sourceEntry =
              old.pinnedContents.find((entry) => entry.id === contentId) ??
              data.find((entry) => entry.id === contentId)

            if (!sourceEntry) {
              return { ...old, data }
            }

            const pinnedEntry = { ...sourceEntry, isPinned: true }
            const pinnedContents = [
              pinnedEntry,
              ...old.pinnedContents.filter((entry) => entry.id !== contentId),
            ]

            return {
              ...old,
              data,
              pinnedContents,
            }
          })
        })

        return { previousQueries }
      },
      onError: (_error: any, _variables, context) => {
        context?.previousQueries.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData)
        })
      },
      onSuccess: () => {
        successToast('Campaign pinned successfully.')
      },
      onSettled: () => {
        queryClient.invalidateQueries(contentKeys.contentLibraryAll())
      },
    }
  )
}

export default usePinContentMutation
